import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Clock, CheckCircle2, AlertTriangle, Loader2, Crosshair, Shield, FileDown, Users, UserPlus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DEFAULT_RADIUS = 150;

export default function StaffApp() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "admin";

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [coords, setCoords] = useState(null);
  const [geoError, setGeoError] = useState(null);
  const [fence, setFence] = useState(null); // {configured, latitude, longitude, radius_m}

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/staff/clock/today").then((r) => r.data),
      api.get("/staff/geofence").then((r) => r.data).catch(() => ({ configured: false })),
    ])
      .then(([ev, f]) => { setEvents(ev); setFence(f); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const requestLocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) { setGeoError("Geolocation not supported in this browser."); return resolve(null); }
      navigator.geolocation.getCurrentPosition(
        (pos) => { const c = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy_m: pos.coords.accuracy }; setCoords(c); setGeoError(null); resolve(c); },
        (err) => { setGeoError(err.message || "Could not get location"); resolve(null); },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    });

  const clock = async (kind) => {
    setBusy(true);
    try {
      const c = await requestLocation();
      await api.post("/staff/clock", { kind, ...(c || {}) });
      toast.success(kind === "in" ? "Clocked in" : "Clocked out");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const lastEvent = events[0];
  const isClockedIn = lastEvent?.kind === "in";

  let minutes = 0, openIn = null;
  for (const e of [...events].reverse()) {
    if (e.kind === "in") openIn = new Date(e.ts);
    else if (e.kind === "out" && openIn) { minutes += Math.max(0, (new Date(e.ts) - openIn) / 60000); openIn = null; }
  }
  if (openIn) minutes += Math.max(0, (new Date() - openIn) / 60000);

  return (
    <div data-testid="staff-app-page" className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Staff App</div>
        <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">GPS clock-in / clock-out</h1>
        <p className="mt-2 text-stone-500">Time on shift today: <strong className="text-stone-900">{Math.floor(minutes / 60)}h {Math.round(minutes % 60)}m</strong></p>
      </header>

      {isManager && <GeofenceCard fence={fence} onSaved={load} />}

      <FenceStatusBanner fence={fence} />

      <section className="rounded-3xl border border-stone-200 bg-stone-900 text-stone-50 p-8">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-emerald-300">
          <MapPin className="w-3.5 h-3.5" />
          {coords ? `On location · accuracy ±${Math.round(coords.accuracy_m)}m` : geoError ? `Location: ${geoError}` : "Location: not requested yet"}
        </div>
        <div className="mt-4 font-display text-5xl font-medium">{new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })}</div>
        <div className="text-sm text-stone-400 mt-1">{isClockedIn ? `Clocked in at ${new Date(lastEvent.ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })}` : "Not clocked in"}</div>
        <div className="mt-6 flex gap-3">
          <Button data-testid="clock-in-btn" disabled={busy || isClockedIn} onClick={() => clock("in")} className="bg-emerald-600 hover:bg-emerald-500 rounded-xl h-12 px-6">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Clock in
          </Button>
          <Button data-testid="clock-out-btn" disabled={busy || !isClockedIn} onClick={() => clock("out")} variant="outline" className="rounded-xl h-12 px-6 border-stone-700 text-stone-50 bg-stone-800 hover:bg-stone-700">
            <Clock className="w-4 h-4 mr-2" /> Clock out
          </Button>
        </div>
      </section>

      {/* iter-57 — Manager-on-behalf clock-in.  Owner/admin can punch any
          staff member in or out (skips the geofence — the manager is
          vouching that the named staffer is on-site). */}
      {isManager && <ManagerClockPanel onChange={load} />}

      {/* iter-51 — Payroll roll-up (owner / admin only).  Aggregates clock
          events across every staff member at the center for the chosen
          period (weekly or biweekly) and prints a one-page worksheet the
          director can use when cutting checks. */}
      {isManager && <PayrollCard />}

      <section>
        <h2 className="font-display text-xl font-medium text-stone-900">Today's events</h2>
        <div className="mt-4 rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="clock-events-list">
          {loading ? <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div> : events.length === 0 ? (
            <div className="py-12 text-center text-sm text-stone-500">No events yet today.</div>
          ) : events.map((e) => (
            <div key={e.id} className="px-5 py-3 flex items-center justify-between border-b border-stone-100 last:border-0">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg grid place-items-center ${e.kind === "in" ? "bg-emerald-700/10 text-emerald-700" : "bg-orange-500/10 text-orange-600"}`}>
                  {e.kind === "in" ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                </div>
                <div className="text-sm font-medium text-stone-900">{e.kind === "in" ? "Clock in" : "Clock out"}</div>
              </div>
              <div className="text-right text-xs text-stone-500">
                <div>{new Date(e.ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })}</div>
                {e.latitude ? (
                  <div>
                    {e.latitude.toFixed(4)}, {e.longitude.toFixed(4)}{" "}
                    <span className="text-stone-400">±{Math.round(e.accuracy_m || 0)}m</span>
                    {typeof e.distance_m === "number" && (
                      <span className="ml-1 text-emerald-700 font-medium">· {Math.round(e.distance_m)}m from center</span>
                    )}
                  </div>
                ) : (
                  <div className="text-orange-600 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> No GPS</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function FenceStatusBanner({ fence }) {
  if (!fence) return null;
  if (fence.configured) {
    return (
      <div data-testid="fence-banner-configured" className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-sm text-emerald-900 flex items-center gap-2">
        <Shield className="w-4 h-4" />
        Geofence active — staff must be within <b>{fence.radius_m}m</b> of the center to clock in.
      </div>
    );
  }
  return (
    <div data-testid="fence-banner-missing" className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-900 flex items-center gap-2">
      <AlertTriangle className="w-4 h-4" />
      No geofence set. Staff can currently clock in from anywhere. Owners/admins can set the center location below.
    </div>
  );
}

function GeofenceCard({ fence, onSaved }) {
  const [lat, setLat] = useState(fence?.latitude ?? "");
  const [lng, setLng] = useState(fence?.longitude ?? "");
  const [radius, setRadius] = useState(fence?.radius_m ?? DEFAULT_RADIUS);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    setLat(fence?.latitude ?? "");
    setLng(fence?.longitude ?? "");
    setRadius(fence?.radius_m ?? DEFAULT_RADIUS);
  }, [fence]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    setPicking(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLat(pos.coords.latitude.toFixed(6)); setLng(pos.coords.longitude.toFixed(6)); setPicking(false); toast.success(`Captured (±${Math.round(pos.coords.accuracy)}m)`); },
      (err) => { setPicking(false); toast.error(err.message || "Could not get location"); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const save = async (e) => {
    e.preventDefault();
    const latN = parseFloat(lat), lngN = parseFloat(lng);
    if (Number.isNaN(latN) || Number.isNaN(lngN)) return toast.error("Latitude and longitude are required.");
    setBusy(true);
    try {
      await api.put("/staff/geofence", { latitude: latN, longitude: lngN, radius_m: parseInt(radius, 10) });
      toast.success("Geofence saved.");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} data-testid="geofence-card" className="rounded-3xl border border-stone-200 bg-white p-6">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-emerald-700">
        <Shield className="w-3.5 h-3.5" /> Center location & geofence
      </div>
      <h2 className="mt-1 font-display text-xl text-stone-900">Set where staff may clock in</h2>
      <p className="mt-1 text-sm text-stone-500">Tap "Use my current location" while you're at the center, then pick a radius.</p>

      <div className="mt-5 grid md:grid-cols-[1fr_1fr_180px_auto] gap-3 items-end">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Latitude</Label>
          <Input data-testid="geofence-lat" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="40.0000" className="mt-2 h-11" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Longitude</Label>
          <Input data-testid="geofence-lng" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="-83.0000" className="mt-2 h-11" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Radius · {radius}m</Label>
          <input data-testid="geofence-radius" type="range" min="50" max="2000" step="25" value={radius} onChange={(e) => setRadius(parseInt(e.target.value, 10))} className="mt-4 w-full accent-emerald-700" />
        </div>
        <Button type="button" data-testid="geofence-use-location-btn" onClick={useMyLocation} disabled={picking} variant="outline" className="rounded-xl h-11">
          {picking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Crosshair className="w-4 h-4 mr-1" />}
          {picking ? "Locating…" : "Use my location"}
        </Button>
      </div>

      <div className="mt-5 flex justify-end">
        <Button type="submit" data-testid="geofence-save-btn" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11 px-6">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save geofence"}
        </Button>
      </div>
    </form>
  );
}


// ---------------------------------------------------------------------------
// iter-51 — Payroll summary card (manager only).  Picks a period (weekly or
// biweekly) and a start date, fetches /api/staff/payroll for the live total
// hours per staff member, and exposes a "Print payroll" button that streams
// the matching PDF.  Built as a separate component so its loading state
// doesn't block the main clock-in card.
// ---------------------------------------------------------------------------
function mondayOfDate(d = new Date()) {
  const dt = typeof d === "string" ? new Date(`${d}T00:00:00`) : new Date(d);
  if (Number.isNaN(dt.getTime())) return new Date().toISOString().slice(0, 10);
  const day = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - day);
  return dt.toISOString().slice(0, 10);
}

function PayrollCard() {
  const [period, setPeriod] = useState("weekly");
  const [startDate, setStartDate] = useState(() => mondayOfDate());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/staff/payroll", { params: { period, start_date: startDate } });
      setSummary(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [period, startDate]);

  const printPdf = async () => {
    setPrinting(true);
    try {
      const r = await api.get("/staff/payroll/pdf", {
        params: { period, start_date: startDate },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPrinting(false); }
  };

  const grand = (summary?.rows || []).reduce((s, r) => s + (r.hours || 0), 0);
  const rangeLabel = summary
    ? `${summary.start_date} → ${summary.end_date}`
    : `${startDate} → ${period === "weekly" ? "+7d" : "+14d"}`;

  return (
    <section data-testid="payroll-card" className="rounded-3xl border border-stone-200 bg-white p-6 sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl grid place-items-center bg-emerald-700/10 text-emerald-700 shrink-0">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">Payroll worksheet</div>
            <h3 className="font-display text-xl text-stone-900">Staff hours · {rangeLabel}</h3>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-500">Period</Label>
            <div className="mt-1 inline-flex rounded-xl bg-stone-100 p-1" role="radiogroup">
              <button type="button" data-testid="payroll-weekly-btn" onClick={() => setPeriod("weekly")}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${period === "weekly" ? "bg-emerald-700 text-white" : "text-stone-600 hover:text-stone-900"}`}>
                Weekly
              </button>
              <button type="button" data-testid="payroll-biweekly-btn" onClick={() => setPeriod("biweekly")}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${period === "biweekly" ? "bg-emerald-700 text-white" : "text-stone-600 hover:text-stone-900"}`}>
                Bi-weekly
              </button>
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-500">Start (Monday)</Label>
            <Input data-testid="payroll-start" type="date" value={startDate} onChange={(e) => setStartDate(mondayOfDate(e.target.value))} className="mt-1 h-10 w-44" />
          </div>
          <Button data-testid="payroll-print-btn" disabled={printing} onClick={printPdf} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-10">
            {printing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
            Print payroll
          </Button>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-stone-100 overflow-hidden" data-testid="payroll-table">
        {loading ? (
          <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
        ) : (summary?.rows || []).length === 0 ? (
          <div className="py-10 text-center text-sm text-stone-500">No clock-in/out events recorded in this period.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-600 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-2.5">Employee</th>
                <th className="text-right px-4 py-2.5">Events</th>
                <th className="text-right px-4 py-2.5">Hours</th>
                <th className="text-right px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {summary.rows.map((r) => (
                <tr key={r.user_id} data-testid={`payroll-row-${r.user_id}`}>
                  <td className="px-4 py-2.5 font-medium text-stone-900">{r.user_name || "—"}</td>
                  <td className="px-4 py-2.5 text-right text-stone-600">{r.events}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-emerald-800">{r.hours.toFixed(2)} hrs</td>
                  <td className="px-4 py-2.5 text-right">
                    {r.open_session
                      ? <span className="inline-flex items-center gap-1 text-amber-700 text-xs"><AlertTriangle className="w-3 h-3" /> open</span>
                      : <span className="text-emerald-700 text-xs font-semibold">OK</span>}
                  </td>
                </tr>
              ))}
              <tr className="bg-stone-50">
                <td className="px-4 py-2.5 font-bold text-stone-900">Grand total</td>
                <td className="px-4 py-2.5"></td>
                <td className="px-4 py-2.5 text-right font-bold text-emerald-800">{grand.toFixed(2)} hrs</td>
                <td className="px-4 py-2.5"></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}



/**
 * Manager Clock Panel
 * -------------------
 * Owner/admin-only panel that lets the director punch any staff member in
 * or out from the front desk.  Useful when a staffer forgot to clock in or
 * doesn't have a phone on them.  Skips the geofence (the manager is
 * vouching the staffer is on-site).
 */
function ManagerClockPanel({ onChange }) {
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [note, setNote] = useState({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", phone: "", role: "staff" });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/staff/roster");
      setRoster(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const punch = async (target, kind) => {
    setBusyId(target.id);
    try {
      const res = await api.post("/staff/clock", {
        kind,
        target_user_id: target.id,
        note: note[target.id] || null,
      });
      toast.success(`${kind === "in" ? "Clocked in" : "Clocked out"} ${res.data?.user_name || target.name}.`);
      setNote((prev) => ({ ...prev, [target.id]: "" }));
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const submitInvite = async () => {
    const name = inviteForm.name.trim();
    const email = inviteForm.email.trim();
    if (!name || !email) {
      toast.error("Name and email are required.");
      return;
    }
    if (!email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setInviteBusy(true);
    try {
      const res = await api.post("/staff/invite", inviteForm);
      const sent = res?.data?.invite?.sent;
      toast.success(sent
        ? `Invite emailed to ${email}. They'll set their password from the link.`
        : `Employee added. (Email send failed — share the invite link manually.)`);
      setInviteOpen(false);
      setInviteForm({ name: "", email: "", phone: "", role: "staff" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInviteBusy(false);
    }
  };

  const resendInvite = async (target) => {
    try {
      await api.post(`/staff/users/${target.id}/resend-invite`);
      toast.success(`Invite re-emailed to ${target.email}.`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeEmployee = async (target) => {
    if (!window.confirm(`Remove ${target.name} from your team? Their past clock events stay for payroll.`)) return;
    setRemovingId(target.id);
    try {
      await api.delete(`/staff/users/${target.id}`);
      toast.success(`${target.name} removed.`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRemovingId(null); }
  };

  return (
    <section data-testid="manager-clock-panel" className="rounded-3xl border border-emerald-200 bg-emerald-50/40 p-6">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-xl bg-emerald-700 text-white grid place-items-center">
          <Users className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-display text-xl font-medium text-stone-900">Your team · clock in / out</h2>
          <p className="text-xs text-stone-600 mt-0.5">Add employees so they can clock in from their phones, or punch them in here when they forget. Geofence is skipped — you're vouching they're on-site.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="add-employee-btn"
            size="sm"
            onClick={() => setInviteOpen(true)}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg h-9 text-xs"
          >
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Add employee
          </Button>
          <Button data-testid="manager-clock-refresh" variant="outline" size="sm" onClick={load} className="rounded-lg h-9 text-xs">
            Refresh
          </Button>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-stone-200 bg-white overflow-hidden">
        {loading ? (
          <div className="py-10 grid place-items-center">
            <Loader2 className="w-5 h-5 animate-spin text-emerald-700" />
          </div>
        ) : roster.length === 0 ? (
          <div className="py-10 text-center text-sm text-stone-500">No staff yet — click <b>Add employee</b> above.</div>
        ) : (
          <div className="divide-y divide-stone-100">
            {roster.map((s) => {
              const isBusy = busyId === s.id;
              const lastLabel = s.last_event_ts ? new Date(s.last_event_ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "no events yet";
              const isPending = !!s.pending_invite;
              return (
                <div key={s.id} data-testid={`roster-row-${s.id}`} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-2.5 h-2.5 rounded-full ${s.clocked_in ? "bg-emerald-500" : "bg-stone-300"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-stone-900 truncate flex items-center gap-1.5">
                        {s.name}
                        <span className="text-[11px] uppercase tracking-widest text-stone-400">{s.role}</span>
                        {isPending && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                            Pending invite
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-stone-500 truncate">
                        {s.clocked_in ? `Clocked in · last event ${lastLabel}` : `Off the clock · last event ${lastLabel}`}
                        {s.hours_today > 0 && <span className="ml-2 text-emerald-700">· {s.hours_today.toFixed(2)}h today</span>}
                      </div>
                    </div>
                  </div>
                  <Input
                    data-testid={`roster-note-${s.id}`}
                    placeholder="Optional note (e.g. 'forgot phone')"
                    value={note[s.id] || ""}
                    onChange={(e) => setNote((prev) => ({ ...prev, [s.id]: e.target.value }))}
                    className="h-9 text-sm sm:w-56"
                  />
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      data-testid={`roster-clock-in-${s.id}`}
                      size="sm"
                      disabled={isBusy || s.clocked_in}
                      onClick={() => punch(s, "in")}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg h-9 text-xs px-3"
                    >
                      {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                      Clock in
                    </Button>
                    <Button
                      data-testid={`roster-clock-out-${s.id}`}
                      size="sm"
                      variant="outline"
                      disabled={isBusy || !s.clocked_in}
                      onClick={() => punch(s, "out")}
                      className="rounded-lg h-9 text-xs px-3 border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                      <Clock className="w-3.5 h-3.5 mr-1" /> Clock out
                    </Button>
                    {isPending && (
                      <Button
                        data-testid={`roster-resend-${s.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => resendInvite(s)}
                        className="rounded-lg h-9 text-xs px-3 border-amber-300 text-amber-700 hover:bg-amber-50"
                        title="Resend invite email"
                      >
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {s.role !== "owner" && (
                      <Button
                        data-testid={`roster-remove-${s.id}`}
                        size="sm"
                        variant="ghost"
                        disabled={removingId === s.id}
                        onClick={() => removeEmployee(s)}
                        className="rounded-lg h-9 text-xs px-2 text-stone-400 hover:text-red-600 hover:bg-red-50"
                        title="Remove from team"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add employee dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md" data-testid="add-employee-dialog">
          <DialogHeader>
            <DialogTitle>Add a new employee</DialogTitle>
            <DialogDescription>
              They'll receive an email with a one-time link to set their password, then they can clock in from their phone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Full name</Label>
              <Input
                data-testid="invite-name-input"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="Jane Smith"
                className="mt-1 h-11"
              />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                data-testid="invite-email-input"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="jane@example.com"
                className="mt-1 h-11"
              />
            </div>
            <div>
              <Label className="text-xs">Phone (optional)</Label>
              <Input
                data-testid="invite-phone-input"
                value={inviteForm.phone}
                onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                placeholder="(614) 555-0123"
                className="mt-1 h-11"
              />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <select
                data-testid="invite-role-select"
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                className="mt-1 w-full h-11 border border-stone-300 rounded-md px-3 text-sm bg-white"
              >
                <option value="staff">Staff (Teacher / Assistant)</option>
                <option value="admin">Admin (Office manager)</option>
              </select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setInviteOpen(false)} className="rounded-xl" disabled={inviteBusy}>Cancel</Button>
            <Button data-testid="invite-submit-btn" onClick={submitInvite} disabled={inviteBusy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
              {inviteBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              Send invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
